import { receiveLoginSubmission } from 'actions/authentication';
import PromiseThrottle from 'promise-throttle';
import { store } from 'store';
import { AuthRefreshTokenRequest } from 'utils/constants/network';

const AbortController = window.AbortController;

const promiseThrottle = new PromiseThrottle({
  requestsPerSecond: 10,
  promiseImplementation: Promise,
});

/** @namespace NetworkUtilities */

/**
 * Network call to be used for API
 * @class
 * @memberof NetworkUtilities
 */
export class APINetworkCall {
  constructor(service, resource, data) {
    this.url = `${process.env.REACT_APP_API_URL}/${service}/${resource}`;
    this.data = data;
  }
}

APINetworkCall.fromURL = (url, data, method) => {
  const newCall = new APINetworkCall();
  newCall.url = url;
  newCall.data = data;
  newCall.method = method;
  return newCall;
};

/**
 * Creates and invokes an API call. Returns the JSON object upon success and throws an error in case of a failure.
 * @function rpc
 * @memberof NetworkUtilities
 * @param {APINetworkCall} networkCall - The network call to be made. @see {@link utils/constants/network}
 * @param {Boolean} excludeVenueHeader - Whether or not you wish to exclude the x-tabb-place header
 * @return {Promise<object>}
 */

var isRefreshing = false;
const controllers = {};

const defaultTimeoutMS = 15000;

export const timeout = (ms, promise) => {
  return new Promise(function (resolve, reject) {
    setTimeout(function () {
      reject(new Error('timeout'));
    }, ms);
    promise.then(resolve, reject);
  });
};

export const callAPI = (
  networkCall,
  excludeVenueHeader = false,
  abortPrevious = false,
  timeoutOverride = 0,
  tokenOverride = null
) => {
  var options = {};

  if (isRefreshing) {
    return Promise.reject('refreshing token');
  }

  if (abortPrevious) {
    const existingController = controllers[networkCall.url];
    const newController = new AbortController();

    if (existingController !== undefined) {
      existingController.abort();
    }

    controllers[networkCall.url] = newController;
    options.signal = newController.signal;
  }

  return promiseThrottle.add(
    _callAPI.bind(
      this,
      networkCall,
      excludeVenueHeader,
      timeoutOverride,
      tokenOverride,
      options
    )
  );
};

const _callAPI = (
  networkCall,
  excludeVenueHeader = false,
  timeoutOverride = 0,
  tokenOverride = null,
  abortOptions = {}
) => {
  if (networkCall.data === undefined && networkCall.method !== 'GET') {
    networkCall.data = {};
  }

  const state = store.getState();

  const headers = { 'content-type': 'application/json' };
  const accessToken = state.authentication.accessToken;
  const defaultVenue = state.venue.defaultVenue;

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  } else if (tokenOverride) {
    headers['Authorization'] = `Bearer ${tokenOverride}`;
  }

  if (defaultVenue && !excludeVenueHeader) {
    headers['x-tabb-place'] = defaultVenue;
  }

  const fetchPromise = fetch(networkCall.url, {
    ...abortOptions,
    method: networkCall.method || 'POST',
    body: networkCall.data ? JSON.stringify(networkCall.data) : undefined,
    headers: new Headers(headers),
  })
    .then(
      res => validateResponse(res),
      validationErr => {
        if (validationErr.message === 'Failed to fetch') {
          const err = { status: 0 };
          throw err;
        } else {
          throw validationErr;
        }
      }
    )
    .then(
      resolved => Promise.resolve(resolved),
      validationErr => {
        if (validationErr.status === 401) {
          return _handleErrorStatus(
            validationErr.status,
            headers,
            networkCall,
            excludeVenueHeader
          );
        } else {
          if (validationErr.status === 0) {
            // Assume this was an offline issue
            throw new Error('timeout');
          } else {
            throw validationErr.json;
          }
        }
      }
    );

  if (timeoutOverride < 0) {
    return fetchPromise;
  } else if (timeoutOverride > 0) {
    return timeout(timeoutOverride, fetchPromise);
  } else {
    return timeout(defaultTimeoutMS, fetchPromise);
  }
};

const _handleErrorStatus = (
  errStatus,
  headers,
  networkCall,
  excludeVenueHeader
) => {
  console.log('Unauthorized. Refreshing token.');
  console.log(errStatus, networkCall);
  const refreshReq = AuthRefreshTokenRequest({});

  isRefreshing = true;

  return fetch(refreshReq.url, {
    method: 'POST',
    body: '',
    headers: new Headers(headers),
  })
    .then(
      res => validateResponse(res),
      validationErr => {
        throw validationErr;
      }
    )
    .then(
      json => {
        isRefreshing = false;
        store.dispatch(receiveLoginSubmission(json.token, null));
        return _callAPI(networkCall, excludeVenueHeader);
      },
      err => {
        isRefreshing = false;
        store.dispatch(receiveLoginSubmission(null));

        window.persistor.purge().then(() => {
          setTimeout(() => {
            window.localStorage.clear();
            window.location.href = '/';
          }, 100);
        });

        return Promise.reject('refresh failed');
      }
    );
};

/**
 * Takes the response object from fetch and ensures it falls into the correct 200 - 299 code range.
 * @memberof NetworkUtilities
 * @param {object} res - The response object from the fetch() api
 */
const validateResponse = res => {
  let json = res.json();

  if (!res.ok) {
    return json.then(err => {
      var errObject;
      if (err.description) {
        // stripe stringifies the error for some reason
        if (
          typeof err.description === 'string' ||
          err.description instanceof String
        ) {
          errObject = new Error(err.description);
        } else {
          errObject = new Error(JSON.parse(err.description).message);
        }
      } else {
        // standard error from our own server
        errObject = new Error('could not make request');
      }

      return Promise.reject({
        json: errObject,
        status: res.status,
      });
    });
  } else {
    return res.status >= 200 && res.status < 300
      ? Promise.resolve(json)
      : json.then(
          Promise.reject({
            json: json,
            status: res.status,
          })
        );
  }
};



// WEBPACK FOOTER //
// ./src/utils/network/index.js